import {
  type ClientGeoAreaProfile,
  type ClientOrganization,
  type ClientTerritorialAuthority,
  ClientTerritorialAuthorityTypeEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useScrollToTop } from '@react-navigation/native';
import { type FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScrollView } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Text } from 'react-native-paper';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';

import { ClientExceptionError } from '@/features/api/hooks/errorMiddleware';
import { useGetTerritorialAuthority } from '@/features/api/hooks/grapevine-organization/useGetTerritorialAuthority';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { GeoAreaDetails } from '@/features/geo-area/components/detail/GeoAreaDetails';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { TerritorialAuthorityDescription } from '@/features/mein-ort/components/territorial-authority/TerritorialAuthorityDescription';
import { splitFacts } from '@/features/mein-ort/components/territorial-authority/territorialAuthorityFacts';
import { TerritorialAuthorityFactsList } from '@/features/mein-ort/components/territorial-authority/TerritorialAuthorityFactsList';
import { TerritorialAuthorityHeader } from '@/features/mein-ort/components/territorial-authority/TerritorialAuthorityHeader';
import { TerritorialAuthorityImageCarousel } from '@/features/mein-ort/components/territorial-authority/TerritorialAuthorityImageCarousel';
import { TerritorialAuthorityOrtsteileChips } from '@/features/mein-ort/components/territorial-authority/TerritorialAuthorityOrtsteileChips';
import { TerritorialAuthorityStatsGrid } from '@/features/mein-ort/components/territorial-authority/TerritorialAuthorityStatsGrid';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { Column } from '@/shared/components/layout/Column';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  content: { flexGrow: 1 },
  scroll: { flex: 1 },
  empty: { marginHorizontal: 16, marginTop: 8 },
  profileContainer: { paddingHorizontal: 16, marginVertical: 16 },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 32
  },
  organizationContent: { gap: 16 }
});

const OrganizationContent: FC<{ organization: ClientOrganization }> = ({
  organization
}) => {
  const { stats, rest } = useMemo(
    () => splitFacts(organization.facts),
    [organization.facts]
  );

  return (
    <Column style={styles.organizationContent}>
      <TerritorialAuthorityHeader organization={organization} />
      <TerritorialAuthorityStatsGrid
        organization={organization}
        stats={stats}
      />
      <TerritorialAuthorityFactsList facts={rest} />
      <TerritorialAuthorityDescription description={organization.description} />
      {hasItems(organization.detailImages) && (
        <TerritorialAuthorityImageCarousel images={organization.detailImages} />
      )}
    </Column>
  );
};

const GeoAreaProfileContent: FC<{ profile: ClientGeoAreaProfile }> = ({
  profile
}) => (
  <View style={styles.profileContainer}>
    <GeoAreaDetails geoAreaProfile={profile} />
  </View>
);

type TerritorialAuthorityBodyProps = {
  territorialAuthority: ClientTerritorialAuthority | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
};

const TerritorialAuthorityBody: FC<TerritorialAuthorityBodyProps> = ({
  territorialAuthority,
  isPending,
  isError,
  error,
  onRetry
}) => {
  const { t } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace'
  });

  if (isPending) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size={48} />
      </View>
    );
  }

  if (isError) {
    if (error instanceof ClientExceptionError && error.httpStatus === 404) {
      return (
        <EmptyCard
          style={styles.empty}
          description={t('territorialAuthority.empty')}
        />
      );
    }
    return (
      <View style={styles.center}>
        <Text variant="titleMedium">{t('state.error.title')}</Text>
        <Text variant="bodyMedium">{t('state.error.subtitle')}</Text>
        <Button mode="contained-tonal" onPress={onRetry}>
          {t('state.error.retry')}
        </Button>
      </View>
    );
  }

  if (
    territorialAuthority?.type ===
      ClientTerritorialAuthorityTypeEnum.Organization &&
    territorialAuthority.organization
  ) {
    return (
      <OrganizationContent organization={territorialAuthority.organization} />
    );
  }

  if (
    territorialAuthority?.type ===
      ClientTerritorialAuthorityTypeEnum.GeoAreaProfile &&
    territorialAuthority.geoAreaProfile
  ) {
    return (
      <GeoAreaProfileContent profile={territorialAuthority.geoAreaProfile} />
    );
  }

  return <></>;
};

export const MeinOrtTerritorialAuthorityTab: FC = () => {
  const theme = useAppTheme();

  const { scrollY, feedInset } = useMeinOrtScroll();

  const scrollRef = useRef<ScrollView>(null);
  useScrollToTop(scrollRef);

  const onScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      'worklet';
      scrollY.value = event.contentOffset.y;
    }
  });

  const { data: person } = useGetPerson();

  const {
    data: territorialAuthority,
    isPending,
    isError,
    error,
    refetch
  } = useGetTerritorialAuthority(person?.homeAreaId);

  const backgroundStyle = {
    backgroundColor: theme.colors.surfaceContainerLowest
  };

  return (
    <Animated.ScrollView
      ref={scrollRef}
      style={[styles.scroll, backgroundStyle]}
      contentContainerStyle={[styles.content, { paddingTop: feedInset }]}
      onScroll={onScroll}
      scrollEventThrottle={16}
      showsVerticalScrollIndicator={false}
    >
      {territorialAuthority?.geoArea && (
        <TerritorialAuthorityOrtsteileChips
          geoArea={territorialAuthority.geoArea}
        />
      )}
      <TerritorialAuthorityBody
        territorialAuthority={territorialAuthority}
        isPending={isPending}
        isError={isError}
        error={error}
        onRetry={() => refetch()}
      />
    </Animated.ScrollView>
  );
};
